import { useState } from 'react';
import { Check, ChevronDown, HelpCircle, Pencil } from 'lucide-react';
import type { Recommendation } from '@shared/types';
import { Badge, Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { severity as sevMap, recommendationStatus as statusMap, TONE_BADGE } from '@/lib/status';
import { formatCompactCop, formatNumber, formatShortDate } from '@shared/format';
import { useApproveRecommendation, useAdjustRecommendation } from '@/lib/hooks';

const EDGE: Record<Recommendation['severity'], string> = {
  critico: 'border-l-danger',
  alto: 'border-l-warning',
  medio: 'border-l-info',
};

export function RecommendationCard({ rec }: { rec: Recommendation }) {
  const [showWhy, setShowWhy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(rec.qtyRecommended);
  const approve = useApproveRecommendation();
  const adjust = useAdjustRecommendation();
  const sev = sevMap[rec.severity];
  const st = statusMap[rec.status];
  const pending = rec.status === 'pendiente';

  return (
    <div className={cn('surface border-l-2 p-4', EDGE[rec.severity])}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-2xs text-ink-faint">{rec.sku}</span>
        <Badge tone={sev.tone}>{sev.label}</Badge>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      <h3 className="mt-2 text-base font-semibold text-ink-strong">{rec.productName}</h3>
      <p className="text-xs text-ink-muted">
        {rec.category} · {rec.supplier}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Metric label="Qty. Recomendada">
          <span className="text-xl font-bold text-ink-strong">{formatNumber(rec.qtyRecommended)}</span>
          {rec.qtyIa != null && rec.qtyIa !== rec.qtyRecommended && (
            <span className="block text-2xs text-info-text">IA: {formatNumber(rec.qtyIa)} → ajust.</span>
          )}
        </Metric>
        <Metric label="Ordenar antes del">
          <span className="text-base font-semibold text-ink-strong">{formatShortDate(rec.orderBefore)}</span>
          {rec.overdue && <span className="ml-1 text-2xs font-bold text-danger-text">¡VENCIDO!</span>}
        </Metric>
        <Metric label="Stock actual">
          <span className="text-base font-semibold text-ink-strong">{rec.currentStock} uds</span>
          <span className="block text-2xs text-ink-faint">{rec.coverageDays}d cobertura</span>
        </Metric>
        <Metric label="Costo est.">
          <span className="text-base font-semibold text-ink-strong">{formatCompactCop(rec.costEstimate)}</span>
          <span className="block text-2xs text-ink-faint">COP</span>
        </Metric>
      </div>

      {rec.decisionNote && (
        <p className="mt-3 rounded-lg border border-hairline bg-subtle p-2.5 text-xs text-ink-muted">{rec.decisionNote}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {pending && !editing && (
            <>
              <Button variant="success" icon={<Check className="h-4 w-4" />} disabled={approve.isPending} onClick={() => approve.mutate(rec.id)}>
                {approve.isPending ? 'Aprobando…' : 'Aprobar'}
              </Button>
              <Button variant="outline" icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => setEditing(true)}>
                Ajustar
              </Button>
            </>
          )}
          {pending && editing && (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={qty}
                min={1}
                onChange={(e) => setQty(Number(e.target.value))}
                className="h-8 w-24 rounded-lg border border-hairline bg-panel px-2 text-sm text-ink-strong focus:border-primary focus:outline-none"
              />
              <Button
                variant="primary"
                disabled={adjust.isPending}
                onClick={() => adjust.mutate({ id: rec.id, qty }, { onSuccess: () => setEditing(false) })}
              >
                Guardar
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            </div>
          )}
          {!pending && rec.decidedBy && (
            <span className={cn('inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium', TONE_BADGE[st.tone])}>
              <Check className="h-3.5 w-3.5" />
              {st.label} por {rec.decidedBy}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowWhy((s) => !s)}
          className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink-strong"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          Por qué esta cantidad
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showWhy && 'rotate-180')} />
        </button>
      </div>

      {showWhy && <p className="mt-3 border-t border-hairline pt-3 text-xs leading-relaxed text-ink-muted">{rec.rationale}</p>}
    </div>
  );
}

function Metric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-2xs uppercase tracking-wide text-ink-faint">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}
